/**
 * Checkout do Paddle (Paddle.js v2, carregado só quando alguém vai pagar). A transação vem da
 * API (POST /v1/workspaces/:id/billing/checkout), com o destino do plano já definido.
 */
import type { CheckoutSession } from '@nolens/domain';
import type { Locale } from '@nolens/i18n';

const SRC = 'https://cdn.paddle.com/paddle/v2/paddle.js';

interface PaddleEvent {
  name?: string;
}

interface PaddleJs {
  Environment: { set(env: 'sandbox'): void };
  Initialize(opts: { token: string; eventCallback: (e: PaddleEvent) => void }): void;
  Checkout: {
    open(opts: {
      transactionId: string;
      customer?: { email: string };
      settings: { displayMode: 'overlay'; locale: string; theme: 'light'; allowLogout: boolean };
    }): void;
  };
}

declare global {
  interface Window {
    Paddle?: PaddleJs;
  }
}

let loading: Promise<PaddleJs> | null = null;

function load(): Promise<PaddleJs> {
  if (window.Paddle) return Promise.resolve(window.Paddle);
  loading ??= new Promise<PaddleJs>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SRC;
    s.async = true;
    s.onload = () => (window.Paddle ? resolve(window.Paddle) : reject(new Error('paddle')));
    s.onerror = () => {
      loading = null;
      s.remove();
      reject(new Error('paddle'));
    };
    document.head.appendChild(s);
  });
  return loading;
}

/** O Paddle.js só se inicializa uma vez: os eventos vão para o checkout aberto agora. */
let initialized: string | null = null;
let onEvent: (e: PaddleEvent) => void = () => {};

export async function openCheckout(
  s: CheckoutSession,
  locale: Locale,
  on: { completed(): void; closed(): void },
): Promise<void> {
  const paddle = await load();
  if (initialized !== s.clientToken) {
    if (s.environment === 'sandbox') paddle.Environment.set('sandbox');
    paddle.Initialize({ token: s.clientToken, eventCallback: (e) => onEvent(e) });
    initialized = s.clientToken;
  }
  onEvent = (e) => {
    if (e.name === 'checkout.completed') on.completed();
    else if (e.name === 'checkout.closed') on.closed();
  };
  paddle.Checkout.open({
    transactionId: s.transactionId,
    ...(s.email ? { customer: { email: s.email } } : {}),
    settings: {
      displayMode: 'overlay',
      locale: locale === 'pt-PT' ? 'pt' : 'en',
      theme: 'light',
      allowLogout: false,
    },
  });
}
